import { createFileRoute, Link, notFound, type ErrorComponentProps } from "@tanstack/react-router";
import { EQUIPMENT } from "@/data/equipment";
import { useReveal } from "@/hooks/use-reveal";
import { trackEvent } from "@/lib/analytics";
import { metaDesc } from "@/lib/seo-description";

import { ReviewsProof } from "@/components/reviews-proof";
import { ORGANIZATION_SCHEMA, absoluteImageUrl } from "@/lib/schema-org";
/**
 * Extrait le montant reellement affiche sur la fiche ("60 €/jour les 2 micros" -> 60).
 * Renvoie null quand aucun prix n'est affiche ("Sur devis") : dans ce cas aucune
 * Offer n'est emise, plutot qu'un prix invente.
 */
function parseDisplayedPrice(priceFrom: string): string | null {
  const m = priceFrom.match(/(\d[\d\s\u00a0]*)\s*€/);
  if (!m) return null;
  const n = m[1].replace(/[\s\u00a0]/g, "");
  return n.length ? n : null;
}

export const Route = createFileRoute("/location-materiel/$slug")({
  loader: ({ params }) => {
    const item = EQUIPMENT.find((e) => e.slug === params.slug);
    if (!item) throw notFound();
    return { item };
  },
  head: ({ loaderData }) => {
    const i = loaderData?.item;
    if (!i) return {};
    // Title overrides per slug for high-impression GSC queries
    const overrides: Record<string, { title: string; desc: string }> = {
      "sono-line-array": {
        title: "Location sono Line Array jusqu'à 2000 pers. | DJ Val",
        desc: "Location d'un système son Line Array pour événements jusqu'à 2000 personnes : Vaucluse, Avignon, Cavaillon. Calage DSP, technicien, livraison et montage inclus.",
      },
      "machine-fumee-lourde": {
        title: "Location machine à fumée lourde Vaucluse | DJ Val",
        desc: "Location de machine à fumée lourde (low fog) pour ouverture de bal à Avignon, Cavaillon et dans tout le Vaucluse. Effet nuage au sol, livraison et installation possibles.",
      },
      "console-pioneer": {
        title: "Location console DJ Pioneer CDJ-3000 | DJ Val",
        desc: "Location de régie Pioneer CDJ-3000 et DJM-A9 à Avignon, Cavaillon et dans le Vaucluse. Le standard club mondial, livré calé et prêt à mixer pour votre soirée.",
      },
      photobooth: {
        title: "Photobooth premium : fiche technique | DJ Val",
        desc: "Fiche technique du photobooth DJ Val : écran tactile, imprimante dye-sub 10×15, accessoires. Formule digitale 190 €, forfait illimité 390 € livré installé.",
      },
      karaoke: {
        title: "Location pack karaoké dès 200 € | DJ Val",
        desc: "Pack karaoké complet à louer dès 200 € : système son, vidéoprojecteur, écran de projection, 2 micros et accès logiciel karaoké en ligne. Cavaillon, Avignon, Vaucluse.",
      },
      "machine-mousse": {
        title: "Location machine à mousse Vaucluse | DJ Val",
        desc: "Location machine à mousse pour soirées dans le Vaucluse et Bouches-du-Rhône. Mousse hypoallergénique, débit pro, livraison Cavaillon, Avignon, Aix.",
      },
      videoprojecteur: {
        title: "Location vidéoprojecteur & écran 5000 lm | DJ Val",
        desc: "Vidéoprojecteur full HD 5000 lumens + écran à louer à Avignon, Cavaillon, Vaucluse. Diaporamas mariage, séminaires, projections événementielles.",
      },
      "par-led": {
        title: "Location éclairage LED : PARs & lyres | DJ Val",
        desc: "Location PARs LED, lyres LED Chauvet pour éclairer salle de mariage, soirée privée ou événement à Avignon, Cavaillon, Vaucluse.",
      },
      "enceintes-actives": {
        title: "Location enceintes actives RCF & Electro-Voice | DJ Val",
        desc: "Location d'enceintes actives RCF ART 745-A et Electro-Voice EKX-15P (1500 W) à Avignon, Cavaillon et dans tout le Vaucluse. Câblage et pieds inclus.",
      },
    };

    const override = overrides[i.slug];
    const title = override?.title ?? `Location ${i.name} | DJ Val`;
    const desc = override?.desc ?? `${i.short}. ${i.text} À partir de ${i.priceFrom}. Livraison-installation dans le Vaucluse, Bouches-du-Rhône et Gard.`;
    return {
      meta: [
        { title },
        { name: "description", content: metaDesc(desc) },
        { property: "og:title", content: title },
        { property: "og:description", content: metaDesc(desc) },
        { property: "og:image", content: absoluteImageUrl(i.image) },
        { property: "og:url", content: `https://dj-val.fr/location-materiel/${i.slug}` },
      ],
      links: [{ rel: "canonical", href: `https://dj-val.fr/location-materiel/${i.slug}` }],
      scripts: [
        { type: "application/ld+json", children: JSON.stringify(buildProductLd(i)) },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: i.faq.map((f: { q: string; a: string }) => ({
              "@type": "Question",
              name: f.q,
              acceptedAnswer: { "@type": "Answer", text: f.a },
            })),
          }),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "Accueil", item: "https://dj-val.fr/" },
              { "@type": "ListItem", position: 2, name: "Location matériel", item: "https://dj-val.fr/location-materiel" },
              { "@type": "ListItem", position: 3, name: i.name, item: `https://dj-val.fr/location-materiel/${i.slug}` },
            ],
          }),
        },
      ],
    };
  },
  notFoundComponent: () => (
    <div className="min-h-[60vh] flex items-center justify-center text-center px-6">
      <div>
        <h1 className="font-serif-display text-4xl text-foreground">Matériel introuvable</h1>
        <Link to="/location-materiel" className="mt-6 inline-block text-[var(--gold)] underline">
          Voir tout le catalogue
        </Link>
      </div>
    </div>
  ),
  errorComponent: ({ error }: ErrorComponentProps) => (
    <div className="min-h-[60vh] flex items-center justify-center text-center px-6">
      <div>
        <h1 className="font-serif-display text-3xl text-foreground">Erreur</h1>
        <p className="mt-3 text-muted-foreground text-sm">{error instanceof Error ? error.message : String(error)}</p>
      </div>
    </div>
  ),
  component: Page,
});

function buildProductLd(item: (typeof EQUIPMENT)[number]) {
  const price = parseDisplayedPrice(item.priceFrom);
  return {
    "@context": "https://schema.org",
    "@type": "Product",
    name: item.name,
    description: item.text,
    image: absoluteImageUrl(item.image),
    category: item.category,
    brand: { "@type": "Brand", name: "DJ Val" },
    ...(price
      ? {
          offers: {
            "@type": "Offer",
            priceCurrency: "EUR",
            price,
            availability: "https://schema.org/InStock",
            seller: ORGANIZATION_SCHEMA,
            areaServed: ["Vaucluse", "Bouches-du-Rhône", "Gard"],
            url: `https://dj-val.fr/location-materiel/${item.slug}`,
            hasMerchantReturnPolicy: {
              "@type": "MerchantReturnPolicy",
              url: "https://dj-val.fr/conditions-de-location",
              applicableCountry: "FR",
              returnPolicyCategory: "https://schema.org/MerchantReturnNotPermitted",
            },
            shippingDetails: {
              "@type": "OfferShippingDetails",
              shippingRate: { "@type": "MonetaryAmount", value: "0", currency: "EUR" },
              shippingDestination: { "@type": "DefinedRegion", addressCountry: "FR" },
              deliveryTime: {
                "@type": "ShippingDeliveryTime",
                handlingTime: { "@type": "QuantitativeValue", minValue: 0, maxValue: 1, unitCode: "DAY" },
                transitTime: { "@type": "QuantitativeValue", minValue: 0, maxValue: 1, unitCode: "DAY" },
              },
            },
          },
        }
      : {}),
  };
}

function Page() {
  useReveal();
  const { item } = Route.useLoaderData();
  const related = EQUIPMENT.filter((e) => e.category === item.category && e.slug !== item.slug).slice(0, 3);

  return (
    <div className="bg-background text-foreground">
      <nav className="pt-28 pb-4 px-6 max-w-6xl mx-auto text-[11px] tracking-[0.18em] uppercase text-muted-foreground">
        <Link to="/" className="hover:text-[var(--gold)]">Accueil</Link>
        <span className="mx-2">/</span>
        <Link to="/location-materiel" className="hover:text-[var(--gold)]">Location</Link>
        <span className="mx-2">/</span>
        <span className="text-foreground">{item.name}</span>
      </nav>

      <section className="px-6 max-w-6xl mx-auto pb-20 pt-4 grid lg:grid-cols-12 gap-10 lg:gap-16 items-end">
        <div className="lg:col-span-5 reveal space-y-6">
          <p className="eyebrow">{item.category}</p>
          <h1 className="font-serif-display text-3xl sm:text-4xl md:text-5xl leading-[1.05] text-foreground">
            {item.name}
          </h1>
          <span className="gold-rule !w-12" />
          <p className="text-base text-muted-foreground font-light leading-relaxed">{item.short}</p>
          <div className="flex items-baseline gap-3 pt-2">
            <span className="text-[10px] uppercase tracking-[0.3em] text-muted-foreground">À partir de</span>
            <span className="font-serif-display text-2xl text-[var(--gold)]">{item.priceFrom}</span>
          </div>
          <div className="flex flex-wrap gap-3 pt-4">
            <Link to="/contactez-nous" className="bg-[var(--gold)] text-foreground px-7 py-3 text-[11px] font-semibold uppercase tracking-[0.25em] hover:bg-foreground transition-colors">
              Demander un devis
            </Link>
            <a href="tel:0767908667" onClick={() => trackEvent("contact_click", { contact_method: "tel", cta_location: "location_materiel_detail" })} className="border border-foreground/20 px-7 py-3 text-[11px] font-semibold uppercase tracking-[0.25em] hover:border-[var(--gold)] hover:text-[var(--gold)] transition-colors">
              07 67 90 86 67
            </a>
          </div>
          <div className="pt-6 border-t border-[var(--gold-line)]">
            <p className="text-[10px] uppercase tracking-[0.3em] text-muted-foreground">Ce que comprend la location</p>
            <ul className="flex flex-wrap gap-2 mt-4">
              {item.specs.slice(0, 6).map((s: { label: string; value: string }) => (
                <li key={s.label} className="text-xs text-foreground/90 border border-[var(--gold-line)] bg-card px-3 py-2">
                  <span className="text-[var(--gold)] mr-2">✦</span>{s.value}
                </li>
              ))}
            </ul>
          </div>
        </div>
        <div className="lg:col-span-7 sr" data-reveal>
          <div className="aspect-[4/3] overflow-hidden bg-card border border-[var(--gold-line)]">
            <img loading="lazy" decoding="async" src={item.image} alt={`Location ${item.name} – DJ Val, Vaucluse`} className="h-full w-full object-cover" />
          </div>
        </div>


      </section>
      <ReviewsProof />

      <section className="border-t border-[var(--gold-line)] bg-card">
        <div className="max-w-6xl mx-auto px-6 py-20 grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-7 sr" data-reveal>
            <p className="eyebrow">Description</p>
            <h2 className="font-serif-display text-3xl text-foreground mt-3">Un matériel pensé pour la scène</h2>
            <span className="gold-rule !w-10 my-5" />
            <p className="text-[15px] text-muted-foreground leading-[1.85] font-light">{item.longText}</p>
            <p className="text-[15px] text-muted-foreground leading-[1.85] font-light mt-5">
              Chaque location inclut le transport en flight-case, la mise en route et le contrôle technique. Pour les configurations complexes, un technicien de DJ Val reste sur place pour garantir une exécution parfaite.
            </p>
            {item.slug === "karaoke" && (
              <p className="text-[15px] text-muted-foreground leading-[1.85] font-light mt-5">
                Pour un événement à Avignon, Cavaillon ou dans le Vaucluse, découvrez la page dédiée :{" "}
                <Link to="/location-karaoke-vaucluse" className="text-[var(--gold)] hover:underline">
                  location pack karaoké Avignon & Vaucluse
                </Link>{" "}
                — livraison, installation et animation en option.
              </p>
            )}
          </div>
          <aside className="lg:col-span-5 sr" data-reveal>
            <div className="border border-[var(--gold-line)] bg-background p-8">
              <p className="eyebrow">Spécifications</p>
              <span className="gold-rule !w-8 my-4" />
              <dl className="divide-y divide-[var(--gold-line)]">
                {item.specs.map((s: { label: string; value: string }) => (
                  <div key={s.label} className="py-3 flex justify-between gap-4">
                    <dt className="text-[11px] uppercase tracking-[0.2em] text-muted-foreground">{s.label}</dt>
                    <dd className="text-sm text-foreground text-right">{s.value}</dd>
                  </div>
                ))}
              </dl>
            </div>
          </aside>
        </div>
      </section>

      {item.gallery?.[0] && (
        <section className="px-6 max-w-6xl mx-auto py-16">
          <div className="sr aspect-[16/9] overflow-hidden border border-[var(--gold-line)] bg-card flex items-center justify-center" data-reveal>
            <img loading="lazy" decoding="async" src={item.gallery[0].src} alt={item.gallery[0].alt} className="h-full w-full object-contain" />
          </div>
        </section>
      )}



      {item.pricingTiers && item.pricingTiers.length > 0 && (
        <section className="px-6 max-w-6xl mx-auto py-20">
          <div className="sr" data-reveal>
            <p className="eyebrow">Tarifs</p>
            <h2 className="font-serif-display text-3xl text-foreground mt-3">{item.pricingTiers.length === 2 ? "Deux" : "Trois"} formules de location</h2>
            <span className="gold-rule !w-10 my-5" />
            <p className="text-sm text-muted-foreground font-light max-w-2xl">
              Tarifs TTC, TVA non applicable (art. 293 B du CGI). La livraison, l'installation et l'animation sont des options qui s'ajoutent au tarif du pack.
            </p>
          </div>
          <div className={`grid gap-px bg-[var(--gold-line)] border border-[var(--gold-line)] mt-8 ${item.pricingTiers.length === 2 ? "md:grid-cols-2" : "md:grid-cols-3"}`}>
            {item.pricingTiers.map((t: { label: string; price: string; detail: string }, i: number) => (
              <div key={t.label} className="sr bg-background p-8 flex flex-col" style={{ transitionDelay: `${i * 80}ms` }} data-reveal>
                <p className="font-serif-display italic text-[var(--gold)] text-xl">{String(i + 1).padStart(2, "0")}</p>
                <h3 className="font-serif-display text-2xl text-foreground mt-3">{t.label}</h3>
                <p className="font-serif-display text-3xl text-[var(--gold)] mt-4" style={{ fontFamily: "Arial, sans-serif", fontVariantNumeric: "tabular-nums" }}>
                  {t.price}
                </p>
                <span className="gold-rule !w-8 my-5" />
                <p className="text-sm text-muted-foreground leading-relaxed font-light">{t.detail}</p>
              </div>
            ))}
          </div>
        </section>
      )}

      <section className="px-6 max-w-6xl mx-auto py-20">

        <div className="sr" data-reveal>
          <p className="eyebrow">Idéal pour</p>
          <h2 className="font-serif-display text-3xl text-foreground mt-3">Où ce matériel fait la différence</h2>
          <span className="gold-rule !w-10 my-5" />
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-8">
          {item.useCases.map((u: string, i: number) => (
            <div key={u} className="sr border-l-2 border-[var(--gold)] pl-5 py-4 bg-card" style={{ transitionDelay: `${i * 60}ms` }} data-reveal>
              <p className="font-serif-display text-lg text-foreground">{u}</p>
            </div>
          ))}
        </div>
      </section>

      {item.gallery?.[1] && (
        <section className="px-6 max-w-6xl mx-auto pb-20">
          <div className="sr aspect-[16/9] overflow-hidden border border-[var(--gold-line)] bg-card" data-reveal>
            <img loading="lazy" decoding="async" src={item.gallery[1].src} alt={item.gallery[1].alt} className="h-full w-full object-cover" />
          </div>
        </section>
      )}



      <section className="border-t border-[var(--gold-line)] bg-card">
        <div className="max-w-3xl mx-auto px-6 py-20">
          <div className="sr text-center" data-reveal>
            <p className="eyebrow">Questions fréquentes</p>
            <h2 className="font-serif-display text-3xl text-foreground mt-3">Bon à savoir</h2>
            <span className="gold-rule mx-auto !w-10 my-5" />
          </div>
          <div className="mt-10 space-y-3">
            {item.faq.map((f: { q: string; a: string }, i: number) => (
              <details key={i} className="group border border-[var(--gold-line)] bg-background open:bg-secondary/40">
                <summary className="cursor-pointer list-none px-6 py-5 flex items-center justify-between gap-4">
                  <span className="font-serif-display text-lg text-foreground">{f.q}</span>
                  <span className="text-[var(--gold)] text-xl group-open:rotate-45 transition-transform">+</span>
                </summary>
                <p className="px-6 pb-6 text-sm text-muted-foreground leading-relaxed font-light">{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {related.length > 0 && (
        <section className="px-6 max-w-6xl mx-auto py-20">
          <div className="sr" data-reveal>
            <p className="eyebrow">Voir aussi</p>
            <h2 className="font-serif-display text-3xl text-foreground mt-3">Dans la même catégorie</h2>
            <span className="gold-rule !w-10 my-5" />
          </div>
          <div className="grid sm:grid-cols-3 gap-6 mt-8">
            {related.map((r, i) => (
              <Link key={r.slug} to="/location-materiel/$slug" params={{ slug: r.slug }} className="sr group block border border-[var(--gold-line)] bg-card hover:border-[var(--gold)] transition-colors" style={{ transitionDelay: `${i * 80}ms` }} data-reveal>
                <div className="aspect-[4/3] overflow-hidden">
                  <img loading="lazy" decoding="async" src={r.image} alt={r.name} className="h-full w-full object-cover group-hover:scale-105 transition-transform duration-700" />
                </div>
                <div className="p-5">
                  <p className="eyebrow text-[10px]">{r.category}</p>
                  <p className="font-serif-display text-lg text-foreground mt-2">{r.name}</p>
                  <p className="text-xs text-muted-foreground mt-1">{r.short}</p>
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}

      <section className="bg-foreground text-background py-20 px-6 text-center">
        <div className="max-w-2xl mx-auto sr" data-reveal>
          <p className="text-[10px] uppercase tracking-[0.35em] text-[var(--gold)]">Disponibilité immédiate</p>
          <h2 className="font-serif-display text-4xl mt-4">
            On <span className="italic text-[var(--gold)]">réserve</span> ?
          </h2>
          <p className="mt-5 text-sm text-background/70 font-light">Livraison-installation à Cavaillon, Avignon, Aix, Luberon, Alpilles. Réponse sous 24 h.</p>
          <div className="mt-8 flex flex-wrap gap-3 justify-center">
            <Link to="/contactez-nous" className="bg-[var(--gold)] text-foreground px-8 py-3 text-[11px] font-semibold uppercase tracking-[0.25em] hover:bg-background transition-colors">
              Demander un devis
            </Link>
            <Link to="/location-materiel" className="border border-background/30 px-8 py-3 text-[11px] font-semibold uppercase tracking-[0.25em] hover:border-[var(--gold)] hover:text-[var(--gold)] transition-colors">
              Voir tout le catalogue
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
